<template>
  <div v-if="visible" :role="role" :class="mergedClass" v-bind="restAttrs">
    <component
      v-if="!noIcon"
      :is="resolvedIcon"
      :size="18"
      :stroke-width="2"
      class="tw:mt-0.5 tw:shrink-0"
      aria-hidden="true" />

    <!-- sr-only variant tag so a screen reader announces "Error:" / "Warning:" etc. before the body,
         giving the context the colour and icon convey for sighted users. -->
    <span class="tw:sr-only">{{ variantLabel }}:</span>

    <div class="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-1.5 tw:text-13">
      <div v-if="title" class="tw:font-semibold">{{ title }}</div>
      <div v-if="$slots.default">
        <slot />
      </div>
      <div v-if="$slots.actions" class="tw:flex tw:flex-wrap tw:gap-2">
        <slot name="actions" />
      </div>
    </div>

    <button
      v-if="closable"
      type="button"
      :aria-label="$t('btn.close')"
      class="tw:-mt-0.5 tw:-mr-1 tw:inline-flex tw:shrink-0 tw:cursor-pointer tw:items-center tw:justify-center tw:rounded-full! tw:border-0 tw:bg-transparent tw:p-0.5 tw:opacity-60 tw:transition-opacity tw:duration-150 tw:ease-in-out tw:hover:opacity-100 tw:focus-visible:opacity-100 tw:focus-visible:outline-none"
      @click="onClose">
      <IconX :size="14" :stroke-width="2" aria-hidden="true" />
    </button>
  </div>
</template>

<script setup lang="ts">
// LeadAlert — static inline alert for errors, info, warnings, success states.
// Uses the same tone palette family as LeadPill (red/orange/success/blue scales).
import type { Component } from 'vue';
import { computed, ref, useAttrs } from 'vue';
import { IconAlertHexagon, IconAlertTriangle, IconCircleCheck, IconInfoCircle, IconX } from '@tabler/icons-vue';
import { cn } from '@/lib/cn';

defineOptions({ inheritAttrs: false });

export type LeadAlertVariant = 'error' | 'info' | 'warning' | 'success';

export interface LeadAlertProps {
  variant?: LeadAlertVariant;
  /** Bold title row rendered above the body. Omit to skip the row. */
  title?: string;
  /** Overrides the variant's default leading icon. */
  icon?: Component;
  /** When true, suppresses the leading icon entirely — even if `icon` is passed. */
  noIcon?: boolean;
  /** When true, renders a trailing ✕ that hides the alert on click. */
  closable?: boolean;
}

const props = withDefaults(defineProps<LeadAlertProps>(), {
  variant: 'error',
  title: undefined,
  icon: undefined,
  noIcon: false,
  closable: false,
});

const emit = defineEmits<{ close: [] }>();

const BASE = 'tw:flex tw:items-start tw:gap-1.5 tw:px-2.5 tw:py-2 tw:rounded-md tw:border tw:w-full';

const VARIANT: Record<LeadAlertVariant, string> = {
  error: 'tw:border-red-200 tw:bg-red-50 tw:text-red-600',
  warning: 'tw:border-orange-200 tw:bg-orange-50 tw:text-orange-600',
  info: 'tw:border-blue-200 tw:bg-blue-50 tw:text-blue-500',
  success: 'tw:border-success-200 tw:bg-success-50 tw:text-success-700',
};

const DEFAULT_ICON: Record<LeadAlertVariant, Component> = {
  error: IconAlertTriangle,
  warning: IconAlertHexagon,
  info: IconInfoCircle,
  success: IconCircleCheck,
};

const visible = ref(true);
const attrs = useAttrs();

const resolvedIcon = computed<Component>(() => props.icon ?? DEFAULT_ICON[props.variant]);
const mergedClass = computed(() => cn(BASE, VARIANT[props.variant], attrs.class as string | undefined));

// Assertive role for errors and warnings so SRs interrupt and announce immediately; polite status
// for info/success, which are informational. Both roles carry implicit aria-live + aria-atomic,
// so there's no need to set those on the element.
const role = computed(() => (props.variant === 'error' || props.variant === 'warning' ? 'alert' : 'status'));

// Prefixed to the slot content in a visually-hidden span so a screen reader hears "Error: …" or
// "Success: …" — restoring the context that colour and icon give sighted users.
const VARIANT_LABEL: Record<LeadAlertVariant, string> = {
  error: 'Error',
  warning: 'Warning',
  info: 'Info',
  success: 'Success',
};
const variantLabel = computed(() => VARIANT_LABEL[props.variant]);
const restAttrs = computed(() => {
  const { class: _c, ...rest } = attrs;
  return rest;
});

function onClose() {
  visible.value = false;
  emit('close');
}
</script>
